import { useState, useEffect, useMemo } from "react";
import { useI18n } from "@/hooks/useI18n";
import { getAreaListApi } from "@/api/common";
import { handleS3UploadFile } from "@/utils/upload";
import { submitKycIdExpiryUpdate } from "@/api/updateKycInfo";
import { toastTip } from "@/components/ui/toast";
import { cloneDeep } from "lodash";
import { isLaterThanNowByAtLeast3Months } from "@/utils/index";
import { useSearchParams } from "react-router-dom";

const UPLOAD_FILE_PREFIX = "personal_updates";

const useHooks = ({ riskLevel, reviewUUID }) => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get("uuid");
  const [inputParams, setInputParams] = useState({
    id_country: "", // 证件发行地
    id_number: "", // 证件号码,
    id_type: "ID_CARD", // 证件类型
    has_other_country_id_card: "", // 是否有其他国家有效身份证
    other_id_country: "", // 其他国家
    other_id_type: "", //// 其他国家/地区 证件类型
    other_id_front_files: [], // 其他国家/地区 正面照
    other_id_opposite_files: [], // 其他国家/地区 反面照
    residence_address: "", // 常居地址
    residence_country: "", // 常居地 国家
    id_copy_files: [],
    clone_address_files: [],
    id_expiry_date: "",
    other_id_expiry_date: "",
    // new
    uuid: "",
    id_document: "",
    other_id_front_document: "",
    other_id_back_document: "",
    address_proof_document: "",
  });

  const [allCountryList, setAllCountryList] = useState([]); // 所有地区
  const [sanctionsCountryList, setSanctionsCountryList] = useState([]); // 制裁地区
  const [noCompliantCountryList, setNoCompliantCountryList] = useState([]); // 非合规
  const [areaScanError, setAreaScanError] = useState(false); // 证件发行地 制裁区错误
  const [areaCompliantError, setAreaCompliantError] = useState(false); // 证件发行地非合规错误
  const [otherCountryAreaError, setOtherCountryAreaError] = useState(false); // 其他国家 不为合规区 错误
  const [uploadLoading, setUploadLoading] = useState(false); // 上传loading
  const [showTipModal, setShowTipModal] = useState(false);
  const [pageStatus, setPageStatus] = useState("INIT");
  const [needUpdateAddress, setNeedUpdateAddress] = useState(false);

  const getAllAreaList = async () => {
    const res = await getAreaListApi();
    if (res.code === 0) {
      const countryList = [...res.data.areas];
      countryList.forEach((item, idx) => {
        item.selectName = item.iso_code2;
        item.value = idx;
      });
      setAllCountryList(countryList);
      setSanctionsCountryList(res.data.sanction_areas);
      setNoCompliantCountryList(res.data.non_compliant_areas);
    }
  };

  useEffect(() => {
    getAllAreaList();
  }, []);

  const inputOnChange = (value, key) => {
    setInputParams((prev) => {
      const SEPECIAL_KEYS = ["wealth_source", "fund_source"];
      if (SEPECIAL_KEYS.indexOf(key) < 0) {
        const obj = { [key]: value };
        return Object.assign({}, prev, obj);
      } else {
        // 增加description
        const newObj = prev[key];
        newObj.description = value;
        return Object.assign({}, prev, {
          [key]: newObj,
        });
      }
    });
  };

  // 上传其他地区国家的正反面证件
  const uploadOtherCountryIdCardFileFn = async (
    file,
    fileTypeSuffix,
    type,
    fileName,
  ) => {
    setUploadLoading(true);
    const uploadRes = await handleS3UploadFile({
      prefix: UPLOAD_FILE_PREFIX,
      file_type: fileTypeSuffix,
      file,
    });
    if (uploadRes.status !== "error") {
      setUploadLoading(false);
      const { filePath } = uploadRes;
      if (type === "front") {
        inputOnChange(
          [
            {
              document_path: filePath,
              document_type: fileTypeSuffix,
              document_error: false,
              document_type_comment: "other_id_front",
              document_name: fileName,
            },
          ],
          "other_id_front_files",
        );
      } else {
        inputOnChange(
          [
            {
              document_path: filePath,
              document_type: fileTypeSuffix,
              document_error: false,
              document_type_comment: "other_id_back",
              document_name: fileName,
            },
          ],
          "other_id_opposite_files",
        );
      }
    } else {
      setUploadLoading(false);
    }
  };

  const handleUploadOtherCountryIdCardFile = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const { size, name } = file;
      const fileTypeSuffix = name
        .split(".")
        [name.split(".").length - 1].toLocaleLowerCase();
      if (
        (fileTypeSuffix === "pdf" || fileTypeSuffix === "jpg") &&
        size / 1000000 < 5
      ) {
        uploadOtherCountryIdCardFileFn(file, fileTypeSuffix, type, name);
      } else {
        if (type === "front") {
          inputOnChange(
            [
              {
                document_path: "",
                document_type: "",
                document_error: true,
                document_type_comment: "other_id_front",
                document_name: name,
              },
            ],
            "other_id_front_files",
          );
        } else {
          inputOnChange(
            [
              {
                document_path: "",
                document_type: "",
                document_error: true,
                document_type_comment: "other_id_back",
                document_name: name,
              },
            ],
            "other_id_opposite_files",
          );
        }
      }
    }
  };

  // 上传地址证明 或证明副本文件
  const handleUoloadEsFile = async ({ e, type }) => {
    const file = e.target.files[0];
    if (file) {
      const { size, name } = file;
      const fileTypeSuffix = name
        .split(".")
        [name.split(".").length - 1].toLocaleLowerCase();
      if (
        (fileTypeSuffix === "pdf" || fileTypeSuffix === "jpg") &&
        size / 1000000 < 5
      ) {
        setUploadLoading(true);
        const uploadRes = await handleS3UploadFile({
          prefix: UPLOAD_FILE_PREFIX,
          file_type: fileTypeSuffix,
          file,
        });
        if (uploadRes.status !== "error") {
          const { filePath } = uploadRes;
          if (type === "ID_COPY_FILE") {
            inputOnChange(
              [
                {
                  document_path: filePath,
                  document_type: fileTypeSuffix,
                  document_error: false,
                  document_type_comment: "id_copy_files",
                  document_name: name,
                },
              ],
              "id_copy_files",
            );
          } else {
            inputOnChange(
              [
                {
                  document_path: filePath,
                  document_type: fileTypeSuffix,
                  document_error: false,
                  document_type_comment: "clone_address_files",
                  document_name: name,
                },
              ],
              "clone_address_files",
            );
          }
          setUploadLoading(false);
        }
      } else {
        if (type === "ID_COPY_FILE") {
          inputOnChange(
            [
              {
                document_path: "",
                document_type: "",
                document_error: true,
                document_type_comment: "id_copy_files",
                document_name: name,
              },
            ],
            "id_copy_files",
          );
        } else {
          inputOnChange(
            [
              {
                document_path: "",
                document_type: "",
                document_error: true,
                document_type_comment: "clone_address_files",
                document_name: name,
              },
            ],
            "clone_address_files",
          );
        }
      }
    }
  };

  const showOtherCountryBlock = useMemo(() => {
    if (areaScanError) {
      return false;
    }
    if (inputParams.id_type === "PASSPORT") {
      if (areaCompliantError || inputParams.other_id_country) {
        return true;
      }
    }
    return false;
  }, [inputParams, areaCompliantError, areaScanError]);

  const sumitPreCheck = () => {
    const _params = cloneDeep(inputParams);
    const {
      other_id_front_files,
      other_id_opposite_files,
      id_number,
      residence_address,
      has_other_country_id_card,
      other_id_country,
      id_copy_files,
      clone_address_files,
      id_expiry_date,
      other_id_expiry_date,
      id_type,
    } = _params;

    if (!id_number) {
      toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
      return;
    }

    if (!id_copy_files?.[0]?.document_path) {
      toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
      return;
    }
    if (id_copy_files[0]?.document_error) {
      return;
    }

    // 护照 + 制裁区
    if (id_type === "PASSPORT" && areaScanError) {
      toastTip(t("mzWsFtJNWfexuuUgYctv"), { type: "error" });
      return;
    }
    // 护照 + 非合规区 + 没有其他其他国家有效身份证
    if (
      id_type === "PASSPORT" &&
      areaCompliantError &&
      (Number(inputParams?.has_other_country_id_card?.value) === 2 ||
        !inputParams?.has_other_country_id_card)
    ) {
      toastTip(t("mzWsFtJNWfexuuUgYctv"), { type: "error" });
      return;
    }
    // 护照 + 非合规区 + 有其他其他国家有效身份证 + 没有选其他国家
    if (
      id_type === "PASSPORT" &&
      areaCompliantError &&
      Number(inputParams?.has_other_country_id_card?.value) === 1 &&
      !inputParams?.other_id_country
    ) {
      toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
      return;
    }
    // 护照 + 非合规区 + 有其他其他国家有效身份证 + 其他国家地区在制裁区、非合规区
    if (
      id_type === "PASSPORT" &&
      areaCompliantError &&
      Number(inputParams?.has_other_country_id_card?.value) === 1 &&
      inputParams.other_id_country &&
      otherCountryAreaError
    ) {
      toastTip(t("mzWsFtJNWfexuuUgYctv"), { type: "error" });
      return;
    }

    // 护照 + 非合规区 + 有其他其他国家有效身份证 + 其他国家地区在合规区 + （!没有选证件类型 ｜｜ ！ 没有上传正面文件信息 || !没有选证件类型）
    if (
      id_type === "PASSPORT" &&
      areaCompliantError &&
      Number(inputParams?.has_other_country_id_card?.value) === 1 &&
      !otherCountryAreaError &&
      (!inputParams.other_id_type ||
        !inputParams?.other_id_front_files?.[0]?.document_path)
    ) {
      toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
      return;
    }

    if (id_type === "PASSPORT") {
      if (!id_expiry_date) {
        toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
        return;
      }
      // error
      if (!isLaterThanNowByAtLeast3Months(id_expiry_date)) {
        toastTip(t("uNPlYoEqFAqudrBt"), { type: "error" });
        return;
      }
    }

    // 其他国家信息
    if (showOtherCountryBlock) {
      if (!has_other_country_id_card) {
        toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
        return false;
      }
      if (Number(has_other_country_id_card.value) === 1) {
        if (otherCountryAreaError) {
          toastTip(t("mzWsFtJNWfexuuUgYctv"), { type: "error" });
          return false;
        }
        if (!other_id_country || !other_id_front_files?.[0]?.document_path) {
          toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
          return false;
        }
      } else if (Number(has_other_country_id_card.value) === 2) {
        toastTip(t("mzWsFtJNWfexuuUgYctv"), { type: "error" });
        return false;
      }
    }

    if (other_id_front_files?.[0]?.document_error) {
      return false;
    }
    if (other_id_opposite_files?.[0]?.document_error) {
      return false;
    }

    // other_id_expiry_date
    if (
      Number(has_other_country_id_card?.value) === 1 &&
      showOtherCountryBlock
    ) {
      if (!other_id_expiry_date) {
        toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
        return;
      }
      // error
      if (!isLaterThanNowByAtLeast3Months(other_id_expiry_date)) {
        toastTip(t("lLZTreJUqkRzBHsiZ"), { type: "error" });
        return;
      }
    }

    if (needUpdateAddress) {
      if (!residence_address || !clone_address_files?.[0]?.document_path) {
        toastTip(t("QZCxVURndUpletXFScR"), { type: "error" });
        return false;
      }
    }

    setInputParams(Object.assign({}, inputParams, _params));

    return true;
  };

  const onSubmit = async () => {
    const params = cloneDeep(inputParams);

    params.uuid = uuid;
    params.id_document = params?.id_copy_files?.[0]?.document_path;
    params.other_id_front_document =
      params?.other_id_front_files?.[0]?.document_path;
    params.other_id_back_document =
      params?.other_id_opposite_files?.[0]?.document_path;

    if (needUpdateAddress) {
      if (params.clone_address_files[0]?.document_path) {
        params.address_proof_document =
          params?.clone_address_files?.[0]?.document_path;
      }
      if (
        (params.id_type === "PASSPORT" && !params.other_id_country) ||
        params.id_type === "ID_CARD"
      ) {
        params.residence_country = params.id_country;
      } else {
        params.residence_country = params.other_id_country;
      }
    } else {
      delete params.residence_address;
      delete params.address_proof_document;
      delete params.residence_country;
    }

    delete params.has_other_country_id_card;
    delete params.clone_address_files;
    // 如果用户从非合规 => 合规地区
    // 删除所有other字段
    if (
      params.id_type === "ID_CARD" ||
      (params.id_type === "PASSPORT" && !params.other_id_country)
    ) {
      delete params.other_id_front_document;
      delete params.other_id_back_document;
      delete params.other_id_type;
      delete params.other_id_country;
      delete params.other_id_expiry_date;
    }

    if (params.id_type === "ID_CARD") {
      delete params.id_expiry_date;
    }

    delete params.id_copy_files;
    delete params.other_id_front_files;
    delete params.other_id_opposite_files;

    const submitRes = await submitKycIdExpiryUpdate(params);
    if (submitRes.code === 0 && submitRes?.data?.ok) {
      setPageStatus("RESULT");
    } else {
      setShowTipModal(false);
    }
  };

  return {
    inputParams,
    inputOnChange,
    allCountryList,
    sanctionsCountryList,
    noCompliantCountryList,
    areaScanError,
    areaCompliantError,
    otherCountryAreaError,
    setAreaScanError,
    setAreaCompliantError,
    uploadOtherCountryIdCardFileFn,
    handleUploadOtherCountryIdCardFile,
    uploadLoading,
    setInputParams,
    setOtherCountryAreaError,
    handleUoloadEsFile,
    onSubmit,
    setShowTipModal,
    showTipModal,
    pageStatus,
    sumitPreCheck,
    showOtherCountryBlock,
    needUpdateAddress,
    setNeedUpdateAddress,
  };
};

export { useHooks };
